import {
  AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, ElementRef, EventEmitter, Injector, Input, NgZone, OnChanges, OnInit,
  Output, ViewChild, afterNextRender, inject,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { Observable, Subscription, catchError, concatMap, forkJoin, from, map, of, switchMap, take } from 'rxjs';

import { PartnerService } from '@app/common/partner/partner.service';
import { openPartnerSheet } from '@app/common/partner/partner-sheet';
import { QuotationService } from '@app/sales/quotation/quotation.service';
import { AlertService } from '@app/shared/alert.service';
import { isPlainLeftClick } from '@app/shared/util';
import type { Attach } from '@app/shared/_models/common';
import { ObjectType } from '@app/shared/constants-server';
import { AttachService } from '@app/shared/attach/attach.service';
import { ImageCarouselComponent } from '@app/shared/attach/image-carousel/image-carousel.component';
import { parseTz } from '@app/shared/time';
import type { C_value } from '@app/shared/_models/common';
import { C_VALUES_DELIVERY_SLOT } from '@app/shared/constants';
import { ValuesService } from '@app/common/values/values.service';
import { DelrunsService } from '@app/logistic/delruns/delruns.service';
import { serverMessage } from '../rental-billing.service';
import { lineClosed } from '../rental-billing.labels';
import { frDay } from '../rental-days';
import { BlDocs, DeliveryDocs, DossierDocs, NO_DOCS, boardOffTip, docsCountOf, dossierDocsOf, lineDocsOf, signedOffTip } from '../rental-line-docs';
import type { Day, RentalLedgerEntry, RentalLineView } from '../rental-billing.model';
import { RentalDecisionDialogsService } from '../rental-decision-dialogs.service';
import type { LineSheetDialogData, RangeDialogData } from '../rental-dialogs.contract';
import { RentalLifecycleStore } from '../rental-lifecycle.store';
import { BlRef, CREDITABLE_STATES, DayWindow, WindowPreset, creditKindAt, creditPiecesAt, dayAtX, dayEndPct, dayPct, interruptedKindAt, knownBounds, ledgerAt, sameMemoLineIds, shiftWindow, stateAt, windowPreset } from '../rental-planning.geometry';
import {
  Bucket, GroupVM, HeadLine, LineRowVM, PartyRef, PlanningParties, PlanningVM, RibbonVM, SummaryItem, bucketCounts, buildPlanning, dayTip, headOf, isPieceState,
  missingTip, partnerLabel, partnerLastFirst, partyIdsOf, pieceText, readableLine, ribbonTip, summaryOf, visibleLinesOf,
} from './rental-planning.view';

// deliver : non livré, à poser sur un BL ; return : reprise à poser sur un BL ; collect : livré, en place, récupérable.
export type PatientLineAction = 'deliver' | 'return' | 'collect';

interface Drag { kind: 'line' | 'group'; ref: number | string; cell: HTMLElement; a: Day; b: Day; sel: HTMLElement | null; }

// Fenêtre des pièces jointes d'un matériel, ouverte au survol de son libellé (JP 28/10 : à la place de l'info-bulle).
interface DocsSection { key: string; title: string; docs: Attach[]; }
interface DocsItem { row: LineRowVM; memo: string; own: Attach[]; others: DocsSection[]; }
// Fenêtre du trombone d'un patient : un bloc par matériel affiché ; collée au trombone, dessous ou dessus selon la place (top ou bottom).
interface DocsPop { key: string; title: string; items: DocsItem[]; anchor: DOMRect; left: number; top: number | null; bottom: number | null; maxHeight: number; }
// Fenêtre des BL d'un patient, au survol de son nom (JP 29/10) : planification, BL signé, tournée ; un lien sans cible est grisé.
interface BlPopItem { id: number; title: string; when: string; signedUrl: string | null; signedTip: string; boardUrl: string | null; boardTip: string; }
interface BlPop { key: string; title: string; items: BlPopItem[]; anchor: DOMRect; left: number; top: number | null; bottom: number | null; maxHeight: number; }

const PRESETS: { kind: WindowPreset; label: string }[] = [
  { kind: '1m', label: '1 m' }, { kind: '3m', label: '3 m' }, { kind: '6m', label: '6 m' }, { kind: '12m', label: '12 m' }, { kind: 'all', label: 'Tout' },
];

// Planning de facturation d'un dossier : frise par matériel et décisions ; le store est fourni et ouvert par l'hôte (E.3).
@Component({
  selector: 'app-rental-planning',
  templateUrl: './rental-planning.component.html',
  styleUrls: ['./rental-planning.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgTemplateOutlet, MatButtonModule, MatIconModule, MatMenuModule, MatProgressSpinnerModule, ImageCarouselComponent],
  host: { '[class.in-dialog]': 'mode === "dialog"' },
})
export class RentalPlanningComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() mode: 'page' | 'dialog' = 'page';
  @Input() visibleLineIds: number[] | null = null; // page : lignes retenues par les filtres de la page ; null = filtres propres (dialogue, page en échec)
  @Input() focusLineId: number | null = null; // « Voir dans le planning » : rangée amenée à l'écran et surlignée 2 s
  @Input() parties: PlanningParties | null | undefined = undefined; // facultatif : bénéficiaire et tiers payeur fournis par l'hôte ; absent, le Planning les lit lui-même
  @Input() docsReload$: Observable<unknown> | null = null; // page : chaque relecture de la pièce relit les pièces jointes (BL signé, impression)
  @Output() decided = new EventEmitter<number>(); // après chaque enregistrement (store.saved$)
  @Output() navigated = new EventEmitter<void>(); // clic simple vers une pièce : l'hôte dialogue se ferme
  @Input() canDeliver = false; // page : « Livrer un matériel » permis (pièce modifiable) ; le + n'existe pas en dialogue
  @Output() deliver = new EventEmitter<string>(); // + d'un patient : la page ouvre « Livrer un matériel » avec son mémo
  @Output() planDelivery = new EventEmitter<number[]>(); // camion d'un patient : la page ouvre la fenêtre de livraison location sur ses matériels
  @Input() lineActions: ReadonlyMap<number, PatientLineAction> | null = null; // page : ce que le camion peut faire de chaque matériel ; absent = pas de camion
  @Output() openBl = new EventEmitter<number>(); // page : « Accéder à la planification » d'un BL, depuis le survol d'un patient

  protected readonly store = inject(RentalLifecycleStore);
  private readonly dialogs = inject(RentalDecisionDialogsService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly quotations = inject(QuotationService);
  private readonly partners = inject(PartnerService);
  private readonly dialog = inject(MatDialog);
  private readonly alert = inject(AlertService);
  private readonly attachService = inject(AttachService);

  @ViewChild('tip') private tipRef?: ElementRef<HTMLElement>;
  @ViewChild('menuTrigger') private menuTrigger?: MatMenuTrigger;
  @ViewChild('docInput') private docInput?: ElementRef<HTMLInputElement>;

  readonly presets = PRESETS;
  preset: WindowPreset | null = null; // aucun bouton enfoncé : fenêtre par défaut
  window: DayWindow | null = null; // jours affichés, le premier et le dernier inclus
  bucket: Bucket = 'current';
  hideClosed = false;
  search = '';
  vm: PlanningVM | null = null;
  summary: SummaryItem[] = [];
  head: HeadLine[] = [];
  counts: Record<Bucket, number> = { current: 0, returned: 0, all: 0 };
  menuPieces: RentalLedgerEntry[] = [];
  rxLines: number[] = []; // lignes dont le contrat exige une prescription : sans elles, pas de bouton (JP 28/10)
  menuAt = { x: 0, y: 0 };

  private trackPx = 0;
  private gridMeasured = false;
  private windowFor = 0; // dossier dont la fenêtre a été posée
  private loadedParties: PlanningParties | null = null;
  private partiesFor = 0;
  private dossierHead: any = null; // en-tête de la pièce, lu avec ses BL et pièces jointes : bénéficiaire et tiers payeur en viennent
  private builtFor: unknown[] = []; // dépendances du dernier vm : un simple début de rechargement (refreshing) ne refait pas la frise
  private pendingFocus: number | null = null;
  private drag: Drag | null = null;
  private tipKey = '';
  private lines = new Map<number, RentalLineView>();
  private visible: RentalLineView[] = [];
  private groups = new Map<string, GroupVM>();

  docs: DossierDocs = NO_DOCS;
  docsLoaded = false;
  docsError: string | null = null; // lecture des pièces jointes et BL en échec : message et [Réessayer]
  groupDocs = new Map<string, number>(); // pièces distinctes des matériels affichés de chaque patient (trombone)
  pop: DocsPop | null = null;
  blPop: BlPop | null = null;
  private blPopTimer = 0;
  private slots: C_value[] = []; // c_values 8 : demi-journée du lien vers le board
  uploading = false;
  private docsKey = ''; // dossier et mode (open ou complet) des pièces lues
  private docsSub?: Subscription;
  private popTimer = 0;
  private uploadFor: number[] = [];
  private thumbs = new Map<number, string>();

  // Flèches de BL cliquables seulement si l'hôte ouvre la planification (page du dossier) ; en dialogue F3, simple repère.
  get blLinks(): boolean { return this.openBl.observed; }

  ngOnInit(): void {
    this.store.changed$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.rebuild());
    this.store.saved$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((n) => this.decided.emit(n));
    this.store.attached$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadDocs(true));
    this.docsReload$?.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.loadDocs(true));
    this.destroyRef.onDestroy(() => { clearTimeout(this.popTimer); clearTimeout(this.blPopTimer); this.thumbs.forEach((u) => URL.revokeObjectURL(u)); });
    if (this.mode === 'page') this.injector.get(ValuesService).getValues$(C_VALUES_DELIVERY_SLOT).pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (s: C_value[]) => this.slots = s ?? [], error: () => this.slots = [] });   // sans eux : lien sur la journée entière
    this.rebuild();
  }

  ngOnChanges(): void {
    if (this.focusLineId != null) this.pendingFocus = this.focusLineId;
    this.rebuild();
  }

  ngAfterViewInit(): void {
    const el = this.host.nativeElement;
    this.zone.runOutsideAngular(() => {
      el.addEventListener('pointermove', this.onHover);
      el.addEventListener('pointerleave', this.hideTip);
      el.addEventListener('pointerdown', this.onPointerDown);
      window.addEventListener('scroll', this.onScroll, { capture: true, passive: true });
      if (typeof ResizeObserver !== 'undefined') {
        // Largeur seule, et mesure à l'image suivante : redessiner dans le rappel relancerait l'observateur (« ResizeObserver loop » en console).
        let width = -1;
        let raf = 0;
        const ro = new ResizeObserver((entries) => {
          const w = entries[entries.length - 1]?.contentRect.width ?? 0;
          if (Math.abs(w - width) < 0.5) return;
          width = w;
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => this.remeasure());
        });
        ro.observe(el);
        this.destroyRef.onDestroy(() => { ro.disconnect(); cancelAnimationFrame(raf); });
      }
    });
    this.destroyRef.onDestroy(() => {
      el.removeEventListener('pointermove', this.onHover);
      el.removeEventListener('pointerleave', this.hideTip);
      el.removeEventListener('pointerdown', this.onPointerDown);
      window.removeEventListener('scroll', this.onScroll, { capture: true });
      this.endDrag();
    });
    this.remeasure();
  }

  // Largeur réelle de la frise : libellés de mois, jours affichés et place des rubans en dépendent.
  remeasure(): void {
    const px = this.measureTrack();
    if (Math.abs(px - this.trackPx) < 0.5) return;
    this.trackPx = px;
    this.rebuild();
    this.cdr.detectChanges();
  }

  private measureTrack(): number {
    const el = this.host.nativeElement;
    const track = el.querySelector<HTMLElement>('.fr-head .fr-days');
    if (track && track.clientWidth > 0) return track.clientWidth;
    const w = el.clientWidth;
    return w > 0 ? Math.max(0, w - Math.min(280, Math.max(260, w * 0.15)) - 2) : 0;
  }

  rebuild(): void {
    const v = this.store.view;
    if (!v) {
      this.vm = null;
      this.builtFor = [];
      this.rxLines = [];
      this.summary = [];
      this.head = headOf(null, this.store.orderId, this.mode, null);
      this.cdr.markForCheck();
      return;
    }
    if (this.windowFor !== v.order_id || !this.window) {
      this.windowFor = v.order_id;
      this.preset = null;
      this.window = windowPreset('default', v.as_of);
      this.loadedParties = null;
      this.partiesFor = 0;
      this.store.ensureUntil(this.window.end);
    }
    // Mémoïsé par la vue (computed_at), les noms, la fenêtre, la largeur, les filtres et les pièces lues.
    const deps = [v, this.store.revision, this.store.beneficiary, this.store.openOnly, this.window, this.trackPx, this.bucket, this.hideClosed, this.search, this.visibleLineIds, this.docs, this.parties, this.loadedParties];
    if (deps.length !== this.builtFor.length || deps.some((d, i) => d !== this.builtFor[i])) {
      this.builtFor = deps;
      const visible = visibleLinesOf(v, this.visibleLineIds, { bucket: this.bucket, hideClosed: this.hideClosed });
      this.visible = visible;
      this.vm = buildPlanning(v, this.window, this.trackPx, visible, this.search, this.store, this.blsOf);
      this.summary = summaryOf(v, this.store, this.store.openOnly);
      this.head = headOf(v, this.store.orderId, this.mode, this.parties !== undefined ? this.parties : this.loadedParties);
      this.counts = bucketCounts(v, this.hideClosed);
      this.lines = new Map(v.lines.map((l) => [l.id, l]));
      this.groups = new Map(this.vm.groups.map((g) => [g.key, g]));
      this.groupDocs = new Map(this.vm.groups.map((g) => [g.key, docsCountOf(this.docs, g.lines.map((r) => this.lines.get(r.id)).filter((l): l is RentalLineView => !!l))]));
      this.rxLines = this.vm.groups.flatMap((g) => g.lines.filter((r) => r.actionable && this.lines.get(r.id)?.params?.prescription_required === true).map((r) => r.id));
      this.tipKey = '';
    }
    this.loadParties();
    this.loadDocs();
    this.cdr.markForCheck();
    if (!this.gridMeasured) {
      this.gridMeasured = true;
      afterNextRender(() => this.remeasure(), { injector: this.injector }); // la frise vient d'apparaître : sa largeur réelle remplace l'estimation
    }
    if (this.pendingFocus != null) afterNextRender(() => this.applyFocus(), { injector: this.injector });
  }

  get showSearch(): boolean {
    return (this.vm?.memoCount ?? 0) >= 2;
  }

  get ownFilters(): boolean {
    return this.visibleLineIds === null;
  }

  // --- Fenêtre ---

  setPreset(kind: WindowPreset): void {
    const v = this.store.view;
    if (!v) return;
    this.preset = kind;
    this.setWindow(windowPreset(kind, v.as_of, knownBounds(v)));
  }

  shift(months: number): void {
    if (this.window) this.setWindow(shiftWindow(this.window, months));
  }

  today(): void {
    const v = this.store.view;
    if (v) this.setWindow(windowPreset(this.preset ?? 'default', v.as_of, knownBounds(v)));
  }

  private setWindow(w: DayWindow): void {
    this.window = w;
    this.store.ensureUntil(w.end); // la frise du serveur doit couvrir le dernier jour affiché
    this.rebuild();
  }

  // --- Filtres propres ---

  // F3 : la vue s'ouvre sans les matériels repris (open) ; « Repris » et « Tout » les font lire.
  setBucket(b: Bucket): void {
    this.bucket = b;
    if (b !== 'current' && this.store.openOnly) this.store.loadClosed();
    this.rebuild();
  }

  toggleClosed(on: boolean): void {
    this.hideClosed = on;
    this.rebuild();
  }

  onSearch(q: string): void {
    this.search = q;
    this.rebuild();
  }

  // --- Décisions ---

  private base(): { store: RentalLifecycleStore; orderId: number } {
    return { store: this.store, orderId: this.store.orderId };
  }

  openRange(d: Omit<RangeDialogData, 'store' | 'orderId'>): void {
    this.hideTip();
    this.dialogs.openRange({ ...this.base(), ...d }).subscribe();
  }

  openSheet(lineId: number, section?: LineSheetDialogData['section']): void {
    this.hideTip();
    this.dialogs.openLineSheet({ ...this.base(), lineId, ...(section ? { section } : {}) }).subscribe();
  }

  addInterruption(): void {
    const v = this.store.view;
    if (!v || !this.vm) return;
    const lineIds = this.vm.memoCount >= 2 ? [] : this.vm.groups.flatMap((g) => g.readableIds);
    this.openRange({ origin: 'toolbar', lineIds });
  }

  // La fenêtre s'ouvre directement, en durée ; on y coche les matériels de l'ordonnance (seul matériel concerné : déjà coché).
  addPrescription(): void {
    const ids = this.rxLines;
    if (!ids.length) return;
    this.hideTip();
    this.dialogs.openPrescription({ ...this.base(), lineId: ids[0], lineIds: ids.length === 1 ? ids : [], endMode: 'duration' }).subscribe();
  }

  // Un geste commercial se saisit ici aussi : c'est une interruption au motif « Geste commercial » (offert).
  groupAdd(g: GroupVM): void {
    this.openRange({ origin: 'group', lineIds: g.readableIds });
  }

  groupEnd(g: GroupVM): void {
    this.hideTip();
    this.dialogs.openWindow({ ...this.base(), bound: 'END', lineIds: g.readableIds }).subscribe();
  }

  // Interruption active (geste compris) : modification ; annulée : détail dans la fiche.
  onRibbon(r: RibbonVM): void {
    const d = r.d;
    if (!d.cancelled) {
      const s = d.sample;
      this.openRange({ origin: 'ribbon', edit: d.entries, lineIds: [...new Set(d.entries.map((e) => e.lineId))], start: s.start, end: s.end });
    } else {
      this.openSheet(d.entries[0].lineId, 'decisions');
    }
  }

  // §7.6 de l'étude : la ligne livrée pendant l'interruption est ajoutée à la décision par le sélecteur.
  onMissing(r: RibbonVM, e: Event): void {
    e.stopPropagation();
    const d = r.d;
    const s = d.sample;
    const lineIds = [...new Set([...d.entries.map((x) => x.lineId), ...d.missing])];
    this.openRange({ origin: 'ribbon', edit: d.entries, lineIds, start: s.start, end: s.end });
  }

  onLineLabel(r: LineRowVM): void {
    this.closePop();
    this.openSheet(r.id);
  }

  onAnomalies(r: LineRowVM, e: Event): void {
    e.stopPropagation();
    this.openSheet(r.id, 'anomalies');
  }

  // Nom du matériel : fiche de son contrat de location ; le reste du libellé ouvre toujours la fiche de la ligne.
  onContract(r: LineRowVM, e: MouseEvent): void {
    e.stopPropagation();
    if (r.contract) this.follow(r.contract.path, e);
  }

  // Article sous l'intitulé du contrat : sa fiche produit (JP 04/11/2026).
  onArticle(r: LineRowVM, e: MouseEvent): void {
    e.stopPropagation();
    if (r.article) this.follow(r.article.path, e);
  }

  // --- Pièces ---

  openPiece(p: RentalLedgerEntry, e: MouseEvent): void {
    this.follow(['/sales/quotation', p.invoice_id], e);
  }

  // Clic simple : navigation (le dialogue hôte se ferme) ; Ctrl, Cmd ou Maj : nouvel onglet, le Planning reste.
  private follow(path: (string | number)[], e: MouseEvent): void {
    if (isPlainLeftClick(e)) {
      this.router.navigate(path);
      this.navigated.emit();
    } else if (e.ctrlKey || e.metaKey || e.shiftKey) {
      const url = this.router.serializeUrl(this.router.createUrlTree(path));
      if (!window.open(url, '_blank')) this.alert.error('Onglet bloqué par le navigateur — autorisez les pop-ups pour ce site');
    }
  }

  pieceLabel(p: RentalLedgerEntry): string {
    return pieceText(p);
  }

  private showPieces(pieces: RentalLedgerEntry[], e: PointerEvent): void {
    if (pieces.length === 1) {
      this.openPiece(pieces[0], e);
      return;
    }
    this.menuPieces = pieces;
    this.menuAt = { x: e.clientX, y: e.clientY };
    this.cdr.detectChanges();
    this.menuTrigger?.openMenu();
  }

  // --- Clic sur un jour (pointerup sans déplacement), dans la zone Angular ---

  private dayClick(drag: Drag, e: PointerEvent): void {
    if (drag.kind === 'group') {
      const g = this.groups.get(drag.ref as string);
      if (g?.readableIds.length) this.openRange({ origin: 'click', lineIds: g.readableIds, start: drag.a, end: null });
      return;
    }
    const l = this.lines.get(drag.ref as number);
    if (!l || l.id <= 0) return;
    const closed = lineClosed(l); // ses pièces restent cliquables ; ailleurs, la fiche dit qu'elle est close
    if (!readableLine(l) && !closed) {
      this.openSheet(l.id, 'anomalies');
      return;
    }
    const state = stateAt(l, drag.a);
    // Jour interrompu, ou rendu avec un avoir prévu ou émis, déjà facturé : l'avoir qui le rend d'abord, sinon la facture qui l'a payé.
    if ((state === 'INTERRUPTED' && interruptedKindAt(l, drag.a) !== 'free') || (state && CREDITABLE_STATES.has(state) && creditKindAt(l, drag.a))) {
      const avoirs = creditPiecesAt(l, drag.a);
      this.showPieces(avoirs.length ? avoirs : ledgerAt(l, drag.a), e);
      return;
    }
    if (isPieceState(state)) {
      const pieces = ledgerAt(l, drag.a);
      if (pieces.length) this.showPieces(pieces, e);
      else this.openSheet(l.id, 'ledger');
      return;
    }
    if (state === 'DUE') {
      this.openSheet(l.id, 'due');
      return;
    }
    if (closed) {
      this.openSheet(l.id);
      return;
    }
    this.openRange({ origin: 'click', lineIds: sameMemoLineIds(l, this.visible), start: drag.a, end: null });
  }

  private dragDone(drag: Drag): void {
    const lo = drag.a < drag.b ? drag.a : drag.b;
    const hi = drag.a < drag.b ? drag.b : drag.a;
    let lineIds: number[] = [];
    if (drag.kind === 'group') {
      lineIds = this.groups.get(drag.ref as string)?.readableIds ?? [];
    } else {
      const l = this.lines.get(drag.ref as number);
      if (l && readableLine(l)) lineIds = sameMemoLineIds(l, this.visible); // voisines affichées seulement, comme le [+] du groupe
    }
    if (lineIds.length) this.openRange({ origin: 'drag', lineIds, start: lo, end: hi }); // jours glissés, le premier et le dernier inclus
  }

  // --- Évènements à haute fréquence : hors zone Angular, DOM direct ---

  private dayUnder(cell: HTMLElement, clientX: number): Day | null {
    if (!this.window) return null;
    const rect = cell.getBoundingClientRect();
    return rect.width > 0 ? dayAtX(clientX - rect.left, rect.width, this.window) : null;
  }

  private onPointerDown = (e: PointerEvent): void => {
    if (e.button !== 0 || !this.window) return;
    const t = e.target as HTMLElement;
    if (t.closest('.ribbon, button, a, input, .fr-lab')) return;
    const lineCell = t.closest<HTMLElement>('.fr-lines');
    const groupCell = lineCell ? null : t.closest<HTMLElement>('.fr-track.grp');
    const cell = lineCell ?? groupCell;
    if (!cell) return;
    const day = this.dayUnder(cell, e.clientX);
    if (!day) return;
    e.preventDefault();
    this.drag = lineCell
      ? { kind: 'line', ref: Number(lineCell.dataset['line']), cell, a: day, b: day, sel: null }
      : { kind: 'group', ref: groupCell!.dataset['group'] ?? '', cell, a: day, b: day, sel: null };
    window.addEventListener('pointermove', this.onDragMove);
    window.addEventListener('pointerup', this.onDragUp);
    window.addEventListener('pointercancel', this.onDragCancel);
  };

  private onDragMove = (e: PointerEvent): void => {
    const d = this.drag;
    if (!d || !this.window) return;
    const day = this.dayUnder(d.cell, e.clientX);
    if (!day || day === d.b) return;
    d.b = day;
    const lo = d.a < d.b ? d.a : d.b;
    const hi = d.a < d.b ? d.b : d.a;
    if (!d.sel) {
      d.sel = document.createElement('div');
      d.sel.className = 'sel'; // créé hors gabarit : les styles du composant ne l'atteignent pas, d'où le style en ligne
      d.sel.style.cssText = 'position:absolute;top:0;bottom:0;z-index:2;pointer-events:none;outline:2px solid var(--primary);outline-offset:-2px;background:rgba(14,110,253,.10)';
      d.cell.appendChild(d.sel);
    }
    const left = dayPct(lo, this.window);
    d.sel.style.left = left + '%';
    d.sel.style.width = dayEndPct(hi, this.window) - left + '%';
  };

  private onDragUp = (e: PointerEvent): void => {
    const d = this.drag;
    this.endDrag();
    if (!d) return;
    this.zone.run(() => (d.a === d.b ? this.dayClick(d, e) : this.dragDone(d)));
  };

  private onDragCancel = (): void => this.endDrag();

  private endDrag(): void {
    window.removeEventListener('pointermove', this.onDragMove);
    window.removeEventListener('pointerup', this.onDragUp);
    window.removeEventListener('pointercancel', this.onDragCancel);
    this.drag?.sel?.remove();
    this.drag = null;
  }

  private onHover = (e: PointerEvent): void => {
    const v = this.store.view;
    const t = e.target as HTMLElement;
    if (!v || !this.window) return this.hideTip();
    let key = '';
    let lines: string[] | null = null;
    const miss = t.closest<HTMLElement>('.miss');
    const rib = t.closest<HTMLElement>('.ribbon');
    const arrow = t.closest<HTMLElement>('.bl-arrow');
    const cell = rib || arrow ? null : t.closest<HTMLElement>('.fr-lines');
    if (arrow) {
      key = 'b|' + arrow.dataset['line'] + '|' + arrow.dataset['bl'] + '|' + arrow.dataset['kind'];
      if (key !== this.tipKey) lines = this.arrowTip(Number(arrow.dataset['line']), Number(arrow.dataset['bl']), arrow.dataset['kind'] === 'return');
    } else if (rib) {
      const g = this.groups.get(rib.dataset['group'] ?? '');
      const r = g?.ribbons.find((x) => x.key === rib.dataset['key']);
      key = (miss ? 'm|' : 'r|') + rib.dataset['group'] + '|' + rib.dataset['key'];
      if (r && key !== this.tipKey) lines = miss ? [...missingTip(r.d, v.lines), 'Clic : Appliquer à ce matériel'] : ribbonTip(r.d, v.lines, this.store);
    } else if (cell) {
      const l = this.lines.get(Number(cell.dataset['line']));
      const day = this.dayUnder(cell, e.clientX);
      if (!l || !day) return this.hideTip();
      key = 'd|' + l.id + '|' + day;
      if (key !== this.tipKey) lines = dayTip(l, day, v, this.store);
    } else {
      return this.hideTip();
    }
    const tip = this.tipRef?.nativeElement;
    if (!tip) return;
    if (lines) {
      this.tipKey = key;
      tip.replaceChildren(...lines.map((text, i) => {
        const div = document.createElement('div');
        if (i === 0) div.style.fontWeight = '500';
        div.textContent = text;
        return div;
      }));
    }
    tip.style.display = 'block';
    const w = tip.offsetWidth;
    const h = tip.offsetHeight;
    const x = Math.max(8, Math.min(e.clientX + 14, window.innerWidth - w - 8));
    const y = e.clientY + 16 + h > window.innerHeight - 8 ? Math.max(8, e.clientY - h - 12) : e.clientY + 16;
    tip.style.left = x + 'px';
    tip.style.top = y + 'px';
  };

  private hideTip = (): void => {
    const tip = this.tipRef?.nativeElement;
    if (tip) tip.style.display = 'none';
    this.tipKey = '';
  };

  // --- Bénéficiaire et tiers payeur : lus sur la pièce et les tiers, comme la F3, quand le Planning est affiché ---

  // L'en-tête vient de la lecture des pièces (loadDocs) : plus de seconde lecture de la pièce pour lui.
  private loadParties(): void {
    const id = this.store.view?.order_id ?? 0;
    const head = this.dossierHead;
    if (this.parties !== undefined || id <= 0 || this.partiesFor === id || this.trackPx <= 0 || Number(head?.id) !== id) return;
    this.partiesFor = id;
    of(partyIdsOf(head)).pipe(
      switchMap((ids) => {
        const one = (pid: number) => this.partners.getPartnerByid(pid).pipe(catchError(() => of(null)));
        const ref = (pid: number) => one(pid).pipe(map((p: any) => ({ id: pid, name: partnerLabel(p, pid) })));
        return forkJoin({
          customerId: of(ids.customer),
          recipient: ids.recipient ? one(ids.recipient) : of(null),
          payers: ids.payers.length ? forkJoin(ids.payers.map(ref)) : of([] as PartyRef[]),
        }).pipe(map((r) => ({ ...r, recipientId: ids.recipient })));
      }),
      catchError(() => of(null)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((r) => {
      if (this.store.view?.order_id !== id) return;
      this.loadedParties = r && { customerId: r.customerId, beneficiary: r.recipientId ? { id: r.recipientId, name: partnerLabel(r.recipient, r.recipientId) } : null, payers: r.payers };
      const patient = r && (r.recipientId ? partnerLastFirst(r.recipient) : this.store.view?.customer_name); // bénéficiaire distinct, sinon le client
      if (patient) this.store.setBeneficiary(id, patient);
      this.rebuild();
    });
  }

  // Nom d'un tiers de l'en-tête : sa fiche en fenêtre ; au retour, l'en-tête relu (un nom a pu changer).
  openPartner(id: number): void {
    openPartnerSheet(this.dialog, id).pipe(takeUntilDestroyed(this.destroyRef)).subscribe((back) => {
      if (!back?.id || this.parties !== undefined) return;
      this.partiesFor = 0;
      this.loadParties();
    });
  }

  // --- Pièces jointes : lues avec les BL (mêmes lignes que le store, open ou complet), rangées par matériel ---

  loadDocs(force = false): void {
    const id = this.store.orderId;
    const key = `${id}|${this.store.openOnly}`;
    if (id <= 0 || (!force && key === this.docsKey)) return;
    // Hôte qui relit la pièce (docsReload$) : il signale aussi les matériels repris chargés ; une seule lecture, la sienne, hormis la toute première.
    if (!force && this.docsReload$ && this.docsKey.startsWith(`${id}|`)) return;
    this.docsKey = key;
    this.docsError = null;
    this.docsSub?.unsubscribe();
    this.docsSub = this.quotations.getQuotationById(id, this.store.openOnly ? '?o=deliveries,attachs,open' : '?o=deliveries,attachs')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (r: any) => {
          if (this.store.orderId !== id) return;
          const order = Array.isArray(r) ? r[0] : r;
          this.dossierHead = order ?? null;
          this.docs = dossierDocsOf(order);
          this.docsLoaded = true;
          this.rebuild();
          if (this.pop) this.showPop(this.pop.key, this.pop.anchor);
          if (this.blPop) this.showBlPop(this.blPop.key, this.blPop.anchor);
        },
        error: (e) => {
          this.docsKey = '';
          this.docsError = `Pièces jointes et BL illisibles : ${serverMessage(e)}`;
          console.error('[RentalPlanning] pièces jointes du dossier illisibles', e);
          this.cdr.markForCheck();
        },
      });
  }

  private blsOf = (lineId: number): BlRef[] => (this.docs.bls.get(lineId) ?? []).map(({ bl, back }) => ({ id: bl.id, kind: back ? 'return' : 'delivery', realdatdel: bl.realdatdel, askdatdel: bl.askdatdel }));

  // Trombone du patient : la pièce est jointe à tous ses matériels affichés.
  attachTo(lineIds: number[], e?: Event): void {
    e?.stopPropagation();
    if (!lineIds.length || this.uploading) return;
    this.uploadFor = lineIds;
    this.docInput?.nativeElement.click();
  }

  groupLineIds(g: GroupVM): number[] {
    return g.lines.map((r) => r.id);
  }

  // Camion d'un patient (JP 29/10) : seulement s'il a du matériel à poser sur un BL ou à récupérer ; sans aucune livraison possible, c'est une reprise (rouge, vers la gauche).
  truckOf(g: GroupVM): { title: string; reprise: boolean } | null {
    const acts = new Set(g.lines.map((r) => this.lineActions?.get(r.id)));
    const deliver = acts.has('deliver'), ret = acts.has('return'), collect = acts.has('collect');
    if (deliver) return { reprise: false, title: collect ? 'Planifier la livraison ou récupérer le matériel de ce patient' : ret ? 'Planifier la livraison ou la reprise de ce patient' : 'Planifier la livraison de ce patient' };
    if (ret) return { reprise: true, title: collect ? 'Planifier la reprise ou récupérer le matériel livré de ce patient' : 'Planifier la reprise de ce patient' };
    return collect ? { reprise: true, title: 'Récupérer le matériel livré de ce patient' } : null;
  }

  // Pièce du dossier (objecttyp ORDER) marquée data.rental_lines, comme les ordonnances ; en série : le serveur calcule le rang par MAX(seq)+1.
  onDocsPicked(e: Event): void {
    const input = e.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = ''; // sans ça, redéposer le même fichier n'émet plus rien
    const ids = this.uploadFor;
    const orderId = this.store.orderId;
    if (!files.length || !ids.length || orderId <= 0) return;
    this.uploading = true;
    this.cdr.markForCheck();
    let refused = 0;
    from(files).pipe(
      concatMap((f) => this.attachService.postAttach$(f, { rental_lines: ids }, ObjectType.ORDER, orderId)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe({
      next: (r: any) => {
        if (r?.success === false) { refused++; this.alert.error(r.error_msg ?? r.msg ?? 'Dépôt refusé'); }
      },
      error: (err) => {
        this.uploading = false;
        console.error('[RentalPlanning] dépôt impossible', err);
        this.alert.error('ERREUR au dépôt du fichier');
        this.loadDocs(true);
      },
      complete: () => {
        this.uploading = false;
        const ok = files.length - refused;
        if (ok > 0) this.alert.success(ok > 1 ? `${ok} documents joints` : 'Document joint');
        this.loadDocs(true);
      },
    });
  }

  // Survol du trombone : ouverture après un court délai ; revenir sur lui ou sur la fenêtre avant 400 ms annule la fermeture (main qui tremble).
  onClipEnter(g: GroupVM, e: MouseEvent): void {
    clearTimeout(this.popTimer);
    if (this.pop?.key === g.key) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    this.popTimer = window.setTimeout(() => this.showPop(g.key, rect), this.pop ? 0 : 200);
  }

  onClipLeave(): void {
    this.schedulePopClose();
  }

  onPopEnter(): void {
    clearTimeout(this.popTimer);
  }

  onPopLeave(): void {
    this.schedulePopClose();
  }

  closePop(): void {
    clearTimeout(this.popTimer);
    if (!this.pop) return;
    this.pop = null;
    this.cdr.markForCheck();
  }

  private schedulePopClose(): void {
    clearTimeout(this.popTimer);
    this.popTimer = window.setTimeout(() => this.closePop(), 400);
  }

  // Collée au trombone (1 px de recouvrement, aucun vide à traverser) : dessous, ou dessus s'il reste moins de 260 px en bas.
  private showPop(key: string, anchor: DOMRect): void {
    const g = this.groups.get(key);
    const items = (g?.lines ?? []).map((r) => this.docsItemOf(r)).filter((x): x is DocsItem => !!x);
    if (!g || !items.length) return this.closePop();
    this.closeBlPop();
    this.withThumbnails(items.flatMap((it) => [...it.own, ...it.others.flatMap((s) => s.docs)]));
    const below = window.innerHeight - anchor.bottom - 8;
    const up = below < 260 && anchor.top - 8 > below;
    this.pop = {
      key, title: g.title, items, anchor, left: Math.max(8, Math.min(anchor.left, window.innerWidth - 340)),
      top: up ? null : anchor.bottom - 1, bottom: up ? window.innerHeight - anchor.top - 1 : null, maxHeight: up ? anchor.top - 8 : below,
    };
    this.cdr.markForCheck();
  }

  private docsItemOf(row: LineRowVM): DocsItem | null {
    const l = this.lines.get(row.id);
    if (!l) return null;
    const ld = lineDocsOf(this.docs, l.id);
    const others: DocsSection[] = [];
    if (ld.rx.length) others.push({ key: 'rx', title: ld.rx.length > 1 ? 'Ordonnances' : 'Ordonnance', docs: ld.rx });
    for (const b of ld.bls) {
      if (!b.docs.length) continue;
      const signed = b.realdatdel ? ` — signé le ${parseTz(b.realdatdel)?.format('DD/MM/YYYY') ?? ''}` : '';
      others.push({ key: 'bl' + b.id, title: `${b.kind === 'return' ? 'Reprise' : 'Livraison'} N°${b.id}${signed}`, docs: b.docs });
    }
    return { row, memo: (l.memo ?? '').trim(), own: ld.own, others };
  }

  // Vignette lue une fois par pièce, reprise à chaque ouverture (même mécanique que quotationdocs).
  private withThumbnails(docs: Attach[]): void {
    for (const a of docs) {
      const id = +a.id!;
      const known = this.thumbs.get(id);
      if (known) { a.thumbnailLocalUrl = known; continue; }
      this.attachService.loadThumbnailImage$(a).pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe((blob: any) => {
        if (!blob) return;
        const url = this.thumbs.get(id) ?? URL.createObjectURL(blob);
        this.thumbs.set(id, url);
        a.thumbnailLocalUrl = url;
        this.cdr.markForCheck();
      });
    }
  }

  // La fenêtre est fixe : elle se ferme dès que la page ou la frise défile sous elle.
  private onScroll = (e: Event): void => {
    if ((!this.pop && !this.blPop) || (e.target instanceof Node && [...this.host.nativeElement.querySelectorAll('.docs-pop')].some((p) => p.contains(e.target as Node)))) return;
    this.zone.run(() => { this.closePop(); this.closeBlPop(); });
  };

  // --- BL d'un patient : survol de son nom (page seulement), même mécanique que le trombone ---

  onPatientEnter(g: GroupVM, e: MouseEvent): void {
    if (this.mode !== 'page') return;
    clearTimeout(this.blPopTimer);
    if (this.blPop?.key === g.key) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    this.blPopTimer = window.setTimeout(() => this.showBlPop(g.key, rect), this.blPop ? 0 : 300);
  }

  onPatientLeave(): void { this.scheduleBlPopClose(); }
  onBlPopEnter(): void { clearTimeout(this.blPopTimer); }
  onBlPopLeave(): void { this.scheduleBlPopClose(); }

  closeBlPop(): void {
    clearTimeout(this.blPopTimer);
    if (!this.blPop) return;
    this.blPop = null;
    this.cdr.markForCheck();
  }

  onOpenBl(id: number): void {
    this.closeBlPop();
    this.openBl.emit(id);
  }

  private scheduleBlPopClose(): void {
    clearTimeout(this.blPopTimer);
    this.blPopTimer = window.setTimeout(() => this.closeBlPop(), 400);
  }

  // BL des matériels affichés du patient (lecture des pièces : o=deliveries,attachs[,open]) ; non signés d'abord par jour demandé, puis signés du plus récent.
  private showBlPop(key: string, anchor: DOMRect): void {
    const g = this.groups.get(key);
    if (!g) return this.closeBlPop();
    this.closePop();
    const bls = new Map<number, { bl: BlDocs; kinds: Set<string> }>();
    for (const r of g.lines) {
      const l = this.lines.get(r.id);
      if (!l) continue;
      for (const b of lineDocsOf(this.docs, l.id).bls) {
        const e = bls.get(b.id) ?? { bl: b, kinds: new Set<string>() };
        e.kinds.add(b.kind === 'return' ? 'Reprise' : 'Livraison');
        bls.set(b.id, e);
      }
    }
    const last = '\uffff';
    const items = [...bls.values()].sort((x, y) => Number(!!x.bl.realdatdel) - Number(!!y.bl.realdatdel)
      || (x.bl.realdatdel ? String(y.bl.realdatdel).localeCompare(String(x.bl.realdatdel)) : String(x.bl.askdatdel ?? last).localeCompare(String(y.bl.askdatdel ?? last)))
      || x.bl.id - y.bl.id).map((e) => this.blPopItemOf(e.bl, [...e.kinds].join(' + ')));
    const below = window.innerHeight - anchor.bottom - 8;
    const up = below < 200 && anchor.top - 8 > below;
    this.blPop = {
      key, title: g.title, items, anchor, left: Math.max(8, Math.min(anchor.left, window.innerWidth - 480)),
      top: up ? null : anchor.bottom - 1, bottom: up ? window.innerHeight - anchor.top - 1 : null, maxHeight: up ? anchor.top - 8 : below,
    };
    this.cdr.markForCheck();
  }

  private blWhen(b: DeliveryDocs): string {
    const day = b.askdatdel ? String(b.askdatdel).slice(0, 10) : null;
    const half = DelrunsService.slotDayOf(b.askslot, this.slots);
    return b.realdatdel ? `signé le ${parseTz(b.realdatdel)?.format('DD/MM/YYYY [à] HH:mm') ?? ''}`
      : day ? `prévu le ${frDay(day)}${half === 1 ? ' · matin' : half === 2 ? ' · après-midi' : ''}` : 'date à fixer';
  }

  private blPopItemOf(b: BlDocs, kind: string): BlPopItem {
    const day = b.askdatdel ? String(b.askdatdel).slice(0, 10) : null;
    const board = DelrunsService.boardLinkOf(day, b.askslot, b.mode, this.slots);
    return {
      id: b.id, title: `${kind} N°${b.id}`, when: this.blWhen(b),
      signedUrl: b.signedPdf ? this.router.serializeUrl(this.router.createUrlTree(['/d/bl', b.id])) : null, signedTip: signedOffTip(b.realdatdel),
      boardUrl: board ? this.router.serializeUrl(this.router.createUrlTree(board, { queryParams: { bl: b.id } })) : null, boardTip: boardOffTip(b.mode, day),
    };
  }

  // Infobulle d'une flèche de BL : mêmes titre et date que la fenêtre « BL du patient ».
  private arrowTip(lineId: number, blId: number, back: boolean): string[] {
    const e = this.docs.bls.get(lineId)?.find((x) => x.bl.id === blId && x.back === back);
    if (!e) return [];
    return [`${back ? 'Reprise' : 'Livraison'} N°${blId}`, this.blWhen(e.bl), ...(this.blLinks ? ['Clic : accéder à la planification'] : [])];
  }

  // --- « Voir dans le planning » ---

  private applyFocus(): void {
    const id = this.pendingFocus;
    if (id == null || !this.vm) return;
    const shown = this.vm.groups.some((g) => g.lines.some((r) => r.id === id));
    if (!shown && this.ownFilters && this.lines.has(id)) {
      this.bucket = 'all';
      this.hideClosed = false;
      this.search = '';
      this.rebuild();
      return;
    }
    this.pendingFocus = null;
    const row = this.host.nativeElement.querySelector<HTMLElement>(`.fr-row.mat[data-line="${id}"]`);
    if (!row) return;
    row.scrollIntoView?.({ block: 'nearest' });
    row.classList.add('flash');
    this.zone.runOutsideAngular(() => setTimeout(() => row.classList.remove('flash'), 2000));
  }
}
